import clsx from 'clsx';
import { useCallback, useEffect, useRef, useState } from 'react';

import * as styles from './styles.css';

interface SelectOption {
  value: string;
  label: string;
}

export function CustomSelect({
  options,
  value,
  onChange,
  placeholder,
  className,
}: {
  options: SelectOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setOpen(o => !o);
      } else if (e.key === 'Escape') {
        setOpen(false);
      } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        const idx = options.findIndex(o => o.value === value);
        const next =
          e.key === 'ArrowDown'
            ? Math.min(idx + 1, options.length - 1)
            : Math.max(idx - 1, 0);
        onChange(options[next].value);
      }
    },
    [options, value, onChange]
  );

  const selected = options.find(o => o.value === value);

  return (
    <div ref={ref} className={clsx(styles.customSelect, className)}>
      <div
        className={styles.customSelectTrigger}
        role="combobox"
        aria-expanded={open}
        tabIndex={0}
        onClick={() => setOpen(o => !o)}
        onKeyDown={handleKeyDown}
      >
        <span style={!selected ? { opacity: 0.5 } : undefined}>
          {selected?.label ?? placeholder ?? 'Select...'}
        </span>
        <span className={styles.customSelectArrow}>
          {open ? '\u25B2' : '\u25BC'}
        </span>
      </div>
      {open && (
        <div className={styles.customSelectDropdown}>
          {options.map(opt => (
            <div
              key={opt.value}
              className={clsx(
                styles.customSelectOption,
                opt.value === value && styles.customSelectOptionActive
              )}
              onClick={() => {
                onChange(opt.value);
                setOpen(false);
              }}
            >
              {opt.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
